import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import clsx from 'clsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { FiltersBar, useDashboardFilters } from '../../components/DashboardFilters';
import { DirectionTag } from '../../components/badges';
import { useAnalytics, useSettings } from '../../api/queries';
import { formatDate, formatPercent } from '../../lib/format';
import { zoneColor, zoneTextClass } from '../../lib/zone';

export function AnalyticsPage() {
  const { filters } = useDashboardFilters();
  const { data, isLoading } = useAnalytics(filters);
  const { data: settings } = useSettings();

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="ანალიტიკა" />
        <FiltersBar />
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      </>
    );
  }

  const trend = data.trend.filter((t) => t.pct !== null);

  return (
    <>
      <PageHeader title="ანალიტიკა" subtitle={`რისკის ზღვარი ${data.riskThreshold}%`} />
      <FiltersBar />

      {/* --- ტრენდი --- */}
      <Card
        title="დასწრების ტრენდი"
        action={<span className="text-[12px] text-ink-mute">ყოველი ჩატარებული დღის მაჩვენებელი</span>}
        className="mb-5"
      >
        {trend.length < 2 ? (
          <EmptyState
            title="ტრენდისთვის მონაცემი ჯერ არ არის"
            description="საჭიროა მინიმუმ ორი ჩატარებული გაკვეთილი."
          />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e7ec" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) => formatDate(v)}
                  minTickGap={28}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => `${v}%`}
                  width={40}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  labelFormatter={(v: string) => formatDate(v)}
                  formatter={(value: number, _n, item) => [
                    `${value}% (${(item.payload as { marked: number }).marked} ჩანაწერი)`,
                    'დასწრება',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke="#0a5a95"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* --- ლექტორები --- */}
      <Card
        title="ლექტორების ჭრილი"
        action={<span className="text-[12px] text-ink-mute">რისკის წილის მიხედვით</span>}
        className="mb-5"
      >
        <Table
          head={['ლექტორი', 'საშუალო', 'სტუდენტი', 'რისკში', 'საშ. streak', 'ჯგუფ(ებ)ი']}
          aligns={['left', 'right', 'right', 'right', 'right', 'left']}
        >
          {[...data.byLecturer]
            .sort((a, b) => b.atRiskPct - a.atRiskPct)
            .map((l) => (
              <tr key={l.id} className="border-t border-line hover:bg-canvas/60">
                <Td className="font-medium text-ink">{l.name}</Td>
                <Td right>
                  <Pill value={l.avg} settings={settings} />
                </Td>
                <Td right className="tabular text-ink-soft">
                  {l.students}
                </Td>
                <Td right>
                  <span
                    className={clsx(
                      'tabular font-medium',
                      l.atRiskPct >= 40 ? 'text-danger' : l.atRiskPct >= 20 ? 'text-warn' : 'text-ink-soft',
                    )}
                  >
                    {l.atRisk} ({l.atRiskPct}%)
                  </span>
                </Td>
                <Td right className="tabular text-ink-soft">
                  {l.avgStreak}
                </Td>
                <Td className="tabular text-[12px] text-ink-mute">{l.groups.join(', ')}</Td>
              </tr>
            ))}
        </Table>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* --- მიმართულებები --- */}
        <Card title="მიმართულების ჭრილი">
          <Table
            head={['მიმართულება', 'საშუალო', 'სტუდ.', 'ჯგუფი', 'რისკში']}
            aligns={['left', 'right', 'right', 'right', 'right']}
          >
            {data.byDirection.map((d) => (
              <tr key={d.id} className="border-t border-line hover:bg-canvas/60">
                <Td>
                  <DirectionTag name={d.name} color={d.color} />
                </Td>
                <Td right>
                  <Pill value={d.avg} settings={settings} />
                </Td>
                <Td right className="tabular text-ink-soft">
                  {d.students}
                </Td>
                <Td right className="tabular text-ink-soft">
                  {d.groups}
                </Td>
                <Td right className={clsx('tabular', d.atRisk > 0 ? 'text-danger' : 'text-ink-soft')}>
                  {d.atRisk}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>

        {/* --- პროექტი --- */}
        <Card title="პროექტის ჭრილი">
          <Table
            head={['პროექტი', 'საშუალო', 'სტუდ.', 'ჯგუფი']}
            aligns={['left', 'right', 'right', 'right']}
          >
            {data.byProject.map((p) => (
              <tr key={p.project.id} className="border-t border-line hover:bg-canvas/60">
                <Td className="font-medium text-ink">
                  <span className="flex items-center gap-2">
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: p.project.color ?? '#cfd4dc' }}
                    />
                    {p.project.name}
                  </span>
                </Td>
                <Td right>
                  <Pill value={p.avg} settings={settings} />
                </Td>
                <Td right className="tabular text-ink-soft">
                  {p.students}
                </Td>
                <Td right className="tabular text-ink-soft">
                  {p.groups}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      {/* --- კურსები --- */}
      <Card title="კურსების ჭრილი" className="mt-5">
        <Table
          head={['კურსი', 'მიმართულება', 'საშუალო', 'სტუდ.', 'ჯგუფი', 'რისკში']}
          aligns={['left', 'left', 'right', 'right', 'right', 'right']}
        >
          {data.byCourse.map((c) => (
            <tr key={c.id} className="border-t border-line hover:bg-canvas/60">
              <Td className="font-medium text-ink">{c.name}</Td>
              <Td className="text-ink-soft">{c.direction}</Td>
              <Td right>
                <Pill value={c.avg} settings={settings} />
              </Td>
              <Td right className="tabular text-ink-soft">
                {c.students}
              </Td>
              <Td right className="tabular text-ink-soft">
                {c.groups}
              </Td>
              <Td right className={clsx('tabular', c.atRisk > 0 ? 'text-danger' : 'text-ink-soft')}>
                {c.atRisk}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      <p className="mt-4 text-[12px] text-ink-mute">
        ყველა საშუალო ითვლება მხოლოდ გაზომვად სტუდენტებზე — შეწყვეტილები და მონაცემის გარეშე
        დარჩენილები არ მონაწილეობს.{' '}
        <Link to="/groups" className="text-brand-700 hover:underline">
          ჯგუფების სია →
        </Link>
      </p>
    </>
  );
}

// ============================================================

function Table({
  head,
  aligns,
  children,
}: {
  head: string[];
  aligns: ('left' | 'right')[];
  children: React.ReactNode;
}) {
  return (
    <div className="-mx-5 overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="border-y border-line bg-canvas/70 text-[11px] uppercase tracking-wide text-ink-mute">
          <tr>
            {head.map((h, i) => (
              <th
                key={h}
                className={clsx(
                  'px-4 py-2.5 font-semibold',
                  aligns[i] === 'right' ? 'text-right' : 'text-left',
                  i === 0 && 'pl-5',
                  i === head.length - 1 && 'pr-5',
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({
  children,
  right,
  className,
}: {
  children: React.ReactNode;
  right?: boolean;
  className?: string;
}) {
  return (
    <td className={clsx('px-4 py-2.5 first:pl-5 last:pr-5', right && 'text-right', className)}>
      {children}
    </td>
  );
}

function Pill({
  value,
  settings,
}: {
  value: number | null;
  settings: { risk_threshold: number; yellow_threshold: number; red_threshold: number } | undefined;
}) {
  if (value === null) return <span className="text-ink-mute">—</span>;
  return (
    <span
      className="tabular inline-block rounded px-2 py-0.5 text-[12px] font-medium text-white"
      style={{ background: zoneColor(value, settings) }}
    >
      {formatPercent(value)}
    </span>
  );
}

export { zoneTextClass };
